import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { WindowInformation } from '../../../services/window-information';
import FormActions from '../../../components/FormActions';
import { dispatchService } from '../../../services/central-api/dispatch';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import FinishEarlyReasonDropdown from '../../../components/dispatch/FinishEarlyReasonDropdown';
import FormInput from '../../../components/FormInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onClose: (success: boolean) => void;
    unit: {
        id: number;
        unitName?: string;
    };
}

function FinishTeamModal({ isModalVisible, setModalVisible, unit, onClose }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [finishEarlyReasonId, setFinishEarlyReasonId] = useState<number>(0);
    const [dailyPoliceLog, setDailyPoliceLog] = useState<string>('');
    const [observations, setObservations] = useState<string>('');

    async function confirmFinish() {
        try {
            await dispatchService.finishUnit(unit.id, {
                dailyPoliceLog: dailyPoliceLog,
                finishEarlyReasonId: finishEarlyReasonId,
                observations: observations != '' ? observations : undefined
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            setModalVisible(false);
            onClose(true);
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function cleanValues() {
        setFinishEarlyReasonId(0);
        setDailyPoliceLog('');
        setObservations('');
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
                cleanValues();
                onClose(false);
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    {unit.unitName &&
                        <MyAppText>{`${translate('finishTeam')}: ${unit.unitName || ''}`}</MyAppText>
                    }
                    <View style={{ zIndex: 2 }}>
                        <FinishEarlyReasonDropdown
                            value={finishEarlyReasonId}
                            setValue={setFinishEarlyReasonId}
                            unitId={unit.id}
                            labelStyle={styles.fieldsLabelStyle}
                            viewStyle={styles.viewStyle}
                            required
                        />
                    </View>
                    <FormInput
                        label={translate('dailyPolicingLog')}
                        placeholder={translate('dailyPolicingLog')}
                        value={dailyPoliceLog}
                        required
                        multiline={true}
                        onChangeText={(val) => setDailyPoliceLog(val)}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={styles.viewStyle}
                    />

                    <FormInput
                        label={translate('observations')}
                        placeholder={translate('observations')}
                        value={observations}
                        multiline={true}
                        onChangeText={(val) => setObservations(val)}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={styles.viewStyle}
                    />
                    <FormActions
                        onSubmit={async () => {
                            setModalVisible(false);
                            await confirmFinish();
                        }}
                        confirmText='finish'
                        onClose={() => {
                            setModalVisible(false);
                            cleanValues();
                            onClose(false);
                        }}
                        disabled={dailyPoliceLog === '' || !finishEarlyReasonId} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    viewStyle: {
        flexDirection: 'column',
        rowGap: 8
    }
}));


export default FinishTeamModal;
